import React, { useEffect, useRef, useState } from "react";
import { Column, Row, Text, Icon } from "@amzn/meridian";
import Textarea from "@amzn/meridian/textarea";
import arrowLeftLargeTokens from "@amzn/meridian-tokens/base/icon/arrow-left-large";
import cameraTokens from "@amzn/meridian-tokens/base/icon/camera";
import closeSmallTokens from "@amzn/meridian-tokens/base/icon/close-small";
import { useAppSelector, useAppDispatch } from "src/store/store";
import { useNavigate } from "react-router-dom";
import { resetStreamingState } from "src/store/inspectionCaptureSlice";
import { setShownHumanText, setShowWorkflowHeader } from "src/store/workflowSlice";
import InspectionService from "src/services/inspectionService";
import ManualCaptureMultipleStep from "./ManualCaptureMultipleStep";
import "./QuestionStep.scss";

interface QuestionStepProps {
  questionId: string;
  onComplete: () => void;
}

const QuestionStep: React.FC<QuestionStepProps> = ({
  questionId,
  onComplete,
}) => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { questions, inspectionId } = useAppSelector((state) => state.workflow);
  const { scannedBarcode } = useAppSelector((state) => state.inspectionCapture);
  const [showCamera, setShowCamera] = useState<boolean>(false);
  const [selectedAnswer, setSelectedAnswer] = useState<string>("");
  const [comments, setComments] = useState<string>("");
  const [evidenceFiles, setEvidenceFiles] = useState<File[]>([]);
  const [evidencePreviews, setEvidencePreviews] = useState<string[]>([]);
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);

  // Keep a ref so the cleanup closure always has the latest list of Blob URLs
  const evidencePreviewsRef = useRef<string[]>([]);
  evidencePreviewsRef.current = evidencePreviews;

  // Revoke all Blob URLs on component unmount to prevent memory leaks
  useEffect(() => {
    return () => {
      evidencePreviewsRef.current.forEach((url) => URL.revokeObjectURL(url));
    };
  }, []);

  const question = questions.find((q) => q.id === questionId);

  useEffect(() => {
    setSelectedAnswer("");
    setComments("");
    setEvidenceFiles([]);
    setIsSubmitting(false);
    evidencePreviewsRef.current.forEach((url) => URL.revokeObjectURL(url));
    setEvidencePreviews([]);
  }, [questionId]);

  useEffect(() => {
    if (!question) {
      return;
    }

    dispatch(setShownHumanText(question.text));
    dispatch(setShowWorkflowHeader(false));

    return () => {
      dispatch(resetStreamingState());
      if (question.mode === "manual") {
        dispatch(setShowWorkflowHeader(true));
      }
    };
  }, [dispatch, question]);

  if (!question) {
    return (
      <Column alignmentHorizontal="center" spacingInset="500">
        <Text type="h300">Question Not Found</Text>
        <Text type="b200">Question ID: {questionId}</Text>
      </Column>
    );
  }

  const handleCameraSubmit = (files: File[]) => {
    setEvidenceFiles((prev) => [...prev, ...files]);
    const newPreviews = files.map((file) => URL.createObjectURL(file));
    setEvidencePreviews((prev) => [...prev, ...newPreviews]);
    setShowCamera(false);
  };

  if (showCamera) {
    return (
      <ManualCaptureMultipleStep
        humanText={question.text}
        activeQuestionIds={[]}
        onComplete={() => setShowCamera(false)}
        onSubmitFiles={handleCameraSubmit}
      />
    );
  }

  const buildBasePayload = (): FormData => {
    const payload = new FormData();
    payload.append("inspection_id", inspectionId);
    payload.append("question_id", questionId);
    payload.append("confirmed_result", selectedAnswer);
    payload.append("is_compliant", selectedAnswer);
    // TODO: Evaluate if this is still needed. I opted to leave this
    // here since there was previously a use case to answer manual questions for
    // each container scanned.
    if (scannedBarcode !== null) {
      payload.append("object_id", scannedBarcode);
    }
    if (comments.trim() !== "") {
      payload.append("comments", comments.trim());
    }
    return payload;
  };

  const handleSubmit = async () => {
    if (!selectedAnswer || isSubmitting) {
      return;
    }
    setIsSubmitting(true);
    const payload = buildBasePayload();
    await InspectionService.recordManualResult(payload);
    dispatch(setShowWorkflowHeader(true));
    onComplete();
  };

  const handleDeleteEvidence = (index: number) => {
    URL.revokeObjectURL(evidencePreviews[index]);
    setEvidenceFiles((prev) => prev.filter((_, i) => i !== index));
    setEvidencePreviews((prev) => prev.filter((_, i) => i !== index));
  };

  const handleImageModeSubmit = async () => {
    if (!selectedAnswer || evidenceFiles.length === 0 || isSubmitting) {
      return;
    }
    setIsSubmitting(true);
    const payload = buildBasePayload();
    evidenceFiles.forEach((file) => {
      payload.append("files", file);
    });
    await InspectionService.recordManualResult(payload);
    dispatch(setShowWorkflowHeader(true));
    onComplete();
  };

  const handleAnswerSelect = (answer: string) => {
    setSelectedAnswer(answer);
  };

  return (
    <Column className="question-step" height="100%" heights={["fill", "fit"]} spacingInset="600 500">
      <Column spacing="500">
        <Row alignmentVertical="center" spacing="300">
          <div className="question-step__header-icon" role="button" onClick={() => navigate("/home")}>
            <Icon tokens={arrowLeftLargeTokens} color="success"/>
          </div>
          <Text type="h200" className="question-step__section-label">{question.category}</Text>
        </Row>

        <Text type="b300">
          {question.text}
        </Text>

        <Row spacing="100">
          <button
            className={`question-step__answer-button ${selectedAnswer === "Yes" ? "question-step__answer-button--selected" : ""}`}
            onClick={() => handleAnswerSelect("Yes")}
          >
            Yes
          </button>
          <button
            className={`question-step__answer-button ${selectedAnswer === "No" ? "question-step__answer-button--selected" : ""}`}
            onClick={() => handleAnswerSelect("No")}
          >
            No
          </button>
        </Row>

        {question.mode !== "manual" && selectedAnswer && (
          <Column spacing="200">
            <Text type="h200" className="question-step__section-label">Evidence</Text>
            <Row spacing="300" wrap="down">
              <div
                className="question-step__evidence-capture"
                onClick={() => setShowCamera(true)}
                role="button"
              >
                <Icon tokens={cameraTokens} color="success" />
              </div>
              {evidencePreviews.map((preview, idx) => (
                <div key={idx} className="question-step__thumbnail-wrapper">
                  <img
                    src={preview}
                    alt={`Evidence ${idx + 1}`}
                    className="question-step__thumbnail"
                  />
                  <button
                    className="question-step__thumbnail-delete"
                    onClick={() => handleDeleteEvidence(idx)}
                    aria-label={`Remove evidence ${idx + 1}`}
                  >
                    <Icon tokens={closeSmallTokens} />
                  </button>
                </div>
              ))}
            </Row>
          </Column>
        )}

        {selectedAnswer && (
          <Column spacing="100">
            <Text type="h200" className="question-step__section-label">Add comments (Optional)</Text>
            <Textarea
              value={comments}
              onChange={(value: string) => setComments(value)}
              placeholder="Add optional comments..."
              rows={1}
            />
          </Column>
        )}
      </Column>

      {question.mode === "manual" ? (
        <button
          className="question-step__submit-button"
          onClick={handleSubmit}
          disabled={!selectedAnswer || isSubmitting}
        >
          {isSubmitting ? "Submitting..." : "Submit"}
        </button>
      ) : (
        <button
          className="question-step__submit-button"
          onClick={handleImageModeSubmit}
          disabled={!selectedAnswer || evidenceFiles.length === 0 || isSubmitting}
        >
          {isSubmitting ? "Submitting..." : "Submit"}
        </button>
      )}
    </Column>
  );
};

export default QuestionStep;
